import axios, { AxiosResponse, Method } from 'axios';
import { sessionModule } from '@/trials/store/modules/session';
import { SysConst as SC } from '@/trials/plugins/const';
import ErrorResponse from '@/trials/domain/objects/errorResponse';
import ErrorDialogData from '@/trials/exceptions/errorDialogData';

export default class Api {
    /**
     * API呼び出しを行っているか
     */
    private static isCalling: boolean = false;

    /**
     * タイムアウト 30秒
     */
    private static timeoutMS: number = 30 * 1000;

    /**
     * エラーコードのテーブル
     * http status code > error_code > error_subcode > messegeKeyの形式
     */
    private static errorCodeTable: any = {
        200: {
            2006: {
                8: 'error.message.file.preprocessedColumCountFew', // 前処理後データの列数が少なすぎます
                9: 'error.message.file.preprocessedColumCountMany', // 前処理後データの列数が多すぎます
            },
            4001: {
                0: 'error.message.unauthorized', // トークンが無効です
            },
            4002: {
                0: 'error.message.default', // 新しいモデル作成要求が存在します
            },
            6001: {
                0: 'error.message.predict.unmatchColumn', // 列名が学習時と一致していません
            },
        },
        400: {
            2001: {
                0: 'error.message.default', // ファイルパートが存在しません
            },
            2002: {
                0: 'error.message.default', // コンテンツタイプが不正です
            },
            2003: {
                0: 'error.message.file.name', // ファイル名が不正です
            },
            2004: {
                0: 'error.message.file.encode', // ファイルのエンコーディングが不正です
            },
            2005: {
                0: 'error.message.file.format', // ファイルのフォーマットが不正です
            },
            2006: {
                1: 'error.message.file.columnCount', // 列数が不正です
                2: 'error.message.file.rowCount', // 行数が不正です
                3: 'error.message.file.dupuricateIndex', // インデックスの値が重複しています
                4: 'error.message.file.dupuricateColumn', // 列名が重複しています
                5: 'error.message.file.labelNa', // ラベルの値に欠損値が含まれています
                6: 'error.message.file.labelValue', // ラベルに0、1以外の値が含まれています
                7: 'error.message.file.allSameLabel', // ラベルの値が全て同じです
                10: 'error.message.file.insufficientAll', // POSとNEGラベルのデータが不足しています
                11: 'error.message.file.insufficientPos', // POSラベルのデータが不足しています
                12: 'error.message.file.insufficientNeg', // NEGラベルのデータが不足しています
            },
            3001: {
                0: 'error.message.default', // 学習データが存在しません
            },
            3002: {
                0: 'error.message.alreadyExec', // 未完了のモデル作成要求が存在します, 未完了の推論実行要求が存在します
            },
            3003: {
                0: 'error.message.default', // WLモデルが存在しません
            },
            5001: {
                0: 'error.message.default', // 推論データが存在しません
            },
        },
        401: {
            1001: {
                0: 'error.message.unauthorized', // トークン不正
            },
        },
        404: {
            3001: {
                0: 'error.message.default', // 学習データが存在しません
            },
            3002: {
                0: 'error.message.default', // 未完了のモデル作成要求が存在します
            },
            3003: {
                0: 'error.message.default', // WLモデルが存在しません
            },
            3004: {
                0: 'error.message.nodata', // 行が存在しません
            },
            5001: {
                0: 'error.message.retry', // 推論データが存在しません
            },
            5003: {
                0: 'error.message.default', // 推論結果が存在しません
            },
        },
        500: 'error.message.default', // internal server error
        800: 'error.message.default', // connection error
        999: 'error.message.default', // 該当しないエラーコード
        nowCalling: 'error.message.nowCalling', // 通信中
    };

    /**
     * API呼び出し
     * @param method string GET | POST
     * @param url string
     * @param params object | FormData
     * @param headers object 追加ヘッダー
     * @param withToken boolean header に api用tokenを付与するか
     */
    private static async requestApi(
        method: Method = 'GET',
        url: string = '',
        params: { [key: string]: string } | FormData = {},
        headers: { [key: string]: string } = {},
        withToken: boolean = true
    ) {
        /**
         * API呼び出し中の場合、ここで終了
         */
        if (this.isCalling) {
            throw new ErrorDialogData(this.errorCodeTable['nowCalling']);
        } else {
            this.isCalling = true;
        }

        /**
         * token付与
         */
        const authToken = sessionModule.authToken;
        if (withToken && authToken != '') {
            headers['Authorization'] = `Bearer ${authToken}`;
        }

        try {
            /**
             * API call
             */
            const response: AxiosResponse = await axios({
                method: method,
                url: url,
                data: params,
                headers: headers,
                timeout: this.timeoutMS,
            });

            /**
             * 内部的なエラー
             * API個別のエラーは各API用メソッド内でエラー処理
             */
            if (response.data.error_code && response.data.error_code != 0) {
                const errorDialogData = this._createErrorDialogData(response);
                throw errorDialogData;
            }

            return response.data;
        } catch (err) {
            if (err instanceof ErrorDialogData) {
                throw err;
            }

            /**
             * connection error
             */
            if (!err.response) {
                const errorDialogData = new ErrorDialogData(this.errorCodeTable[800]);
                throw errorDialogData;
            }

            /**
             * 40X, 50X系 error
             */
            if (err.response.status >= 400) {
                const errorDialogData = this._createErrorDialogData(err.response);
                if (err.response.status == 401 || err.response.status >= 500) {
                    // 認証エラー、internal server error は
                    // ダイアログを閉じた後にブラウザリロード
                    errorDialogData.needCloseAndReload = true;

                    // 承認エラーであればトークンクリア
                    if (err.response.status == 401) {
                        errorDialogData.needClearToken = true;
                    }
                }
                throw errorDialogData;
            }

            throw err;
        } finally {
            this.isCalling = false;
        }
    }

    /**
     * エラーダイアログ用データを生成
     * @param response レスポンスデータ
     */
    private static _createErrorDialogData(response: AxiosResponse): ErrorDialogData {
        const res = new ErrorResponse(response);
        const statusCode = res.httpStatusCode;
        const code = res.code;
        const subcode = res.subcode;

        /**
         * エラーコードテーブルからmessageKeyを取得
         */
        if (
            this.errorCodeTable[statusCode] &&
            this.errorCodeTable[statusCode][code] &&
            this.errorCodeTable[statusCode][code][subcode]
        ) {
            return new ErrorDialogData(this.errorCodeTable[statusCode][code][subcode], res);
        }

        /**
         * 想定していないエラーなので、ページリロード
         */
        const err = new ErrorDialogData(this.errorCodeTable[999]);
        err.needCloseAndReload = true;
        return err;
    }

    /**
     * API用token取得
     */
    public static async getToken(): Promise<string> {
        const url = SC.Api.Path.GET_TOKEN;
        const response = await this.requestApi('POST', url, {}, {}, false);
        return response.token;
    }

    /**
     * 学習データ用CSVファイルをサーバーに送信して登録
     * @param csv File 学習データ用csvファイルオブジェクト
     */
    public static async registTrainingSet(csv: File): Promise<void> {
        const url = SC.Api.Path.REGIST_TRAINING_SETS;
        const formdata = new FormData();
        formdata.append('file', csv, csv.name);

        await this.requestApi('POST', url, formdata);
    }

    /**
     * 学習データ用CSVファイルとして使用する事例IDをサーバー送信して登録
     * @param name string 学習データ名
     * @param language string 学習データ言語
     */
     public static async selectTrainingSet(name: string, language: string): Promise<void> {
        const url = SC.Api.Path.SELECT_TRAINING_SETS;
        const data = {name, language}

        await this.requestApi('POST', url, data);
    }

    /**
     * 最後にアップロードした学習データを取得
     */
    public static async getTrainingSet(): Promise<any[]> {
        const url = SC.Api.Path.GET_TRAINING_SETS;

        const response = await this.requestApi('GET', url);
        return response;
    }

    /**
     * 学習開始(WLモデル作成要求)
     */
    public static async startCreateModel(): Promise<void> {
        const url = SC.Api.Path.START_CREATE_MODEL;

        await this.requestApi('POST', url);
    }

    /**
     * 最後に作成完了したWLモデルの行毎のナレッジチャンク取得
     */
    public static async getPersonalChunksForTraingSet(rowNumber: number): Promise<any[]> {
        const url = `${SC.Api.Path.GET_KC_FOR_TRAINING_SET}${rowNumber}`;
        const response = await this.requestApi('GET', url);
        return response;
    }

    /**
     * 作成が完了したWLモデルのナレッジチャンク取得
     */
    public static async getKcListForModel(): Promise<any[]> {
        const url = SC.Api.Path.GET_KC_LIST_FOR_MODEL;

        const response = await this.requestApi('GET', url);
        return response;
    }

    /**
     * 最後に作成が完了したWLモデルの学習パラメータとスコア取得
     */
    public static async getParams(): Promise<any[]> {
        const url = SC.Api.Path.GET_PARAMS_AND_SCORES;

        const response = await this.requestApi('GET', url);
        return response;
    }

    /**
     * 作成が完了したWLモデルの前処理済みデータ取得
     */
    public static async getPreprocessedTrainingSet(): Promise<any[]> {
        const url = SC.Api.Path.GET_PREPROCESSED_TRAINING_SETS;

        const response = await this.requestApi('GET', url);
        return response;
    }

    /**
     * 最後に作成要求したWLモデルのステータス取得
     */
    public static async getModelCreationState(): Promise<string> {
        const url = SC.Api.Path.GET_MODEL_CREATION_STATE;

        try {
            const response = await this.requestApi('GET', url);
            return response.status;
        } catch (err) {
            if (err instanceof ErrorDialogData) {
                if (err.errResponse && err.errResponse.code == 4001) {
                    // トークン無効の場合は、リロード
                    err.needClearToken = true;
                    err.needCloseAndReload = true;
                }
            }

            throw err;
        }
    }

    /**
     * 予測データ用CSVファイルをサーバーに送信して登録
     * @param csv File 予測データ用csvファイルオブジェクト
     */
    public static async registPredictSet(csv: File): Promise<void> {
        const url = SC.Api.Path.REGIST_PREDICTION_SETS;
        const formdata = new FormData();
        formdata.append('file', csv, csv.name);

        try {
            await this.requestApi('POST', url, formdata);
        } catch (err) {
            if (err instanceof ErrorDialogData && err.errResponse) {
                // 400
                if (
                    err.errResponse.httpStatusCode == 400 &&
                    [2001, 2002].includes(err.errResponse.code)
                ) {
                    err.messageKey = 'error.message.retry';
                }
            }

            throw err;
        }
    }

    /**
     * 予測データ用CSVファイルとして使用する事例IDをサーバー送信して登録
     * @param name string 予測データ名
     * @param language string 予測データ言語
     */
     public static async selectPredictSet(name: string, language: string): Promise<void> {
        const url = SC.Api.Path.SELECT_PREDICTION_SETS;
        const data = {name, language}

        await this.requestApi('POST', url, data);
    }

    /**
     * 最後にアップロードした予測データを取得
     */
    public static async getPredictSet(): Promise<any[]> {
        const url = SC.Api.Path.GET_PREDICTION_SETS;

        const response = await this.requestApi('GET', url);
        return response;
    }

    /**
     * 予測開始
     */
    public static async startPredict(): Promise<void> {
        const url = SC.Api.Path.START_PREDICTION;

        await this.requestApi('POST', url);
    }

    /**
     * 最後に実行完了した予測の行毎のナレッジチャンク取得
     */
    public static async getPersonalChunksForPredictSet(rowNumber: number): Promise<any[]> {
        const url = `${SC.Api.Path.GET_KC_FOR_PREDICTION_SET}${rowNumber}`;

        try {
            const response = await this.requestApi('GET', url);
            return response;
        } catch (err) {
            if (err instanceof ErrorDialogData && err.errResponse) {
                if (err.errResponse.code == 5003) {
                    err.needCloseAndReload = true;
                }
            }

            throw err;
        }
    }

    /**
     * 最後に実行完了した予測の前処理済みデータ取得
     */
    public static async getPreprocessedPredictSet(): Promise<any[]> {
        const url = SC.Api.Path.GET_PREPROCESSED_PREDICTION_SETS;

        const response = await this.requestApi('GET', url);
        return response;
    }

    /**
     * 最後に実行完了した予測の結果データ取得
     */
    public static async getPredictResult(): Promise<any[]> {
        const url = SC.Api.Path.GET_PREDICTION_RESULT;

        const response = await this.requestApi('GET', url);
        return response;
    }

    /**
     * 最後に実行要求した予測のステータス取得
     */
    public static async getPredictState(): Promise<string> {
        const url = SC.Api.Path.GET_PREDICTION_STATE;

        try {
            const response = await this.requestApi('GET', url);
            return response.status;
        } catch (err) {
            if (err instanceof ErrorDialogData) {
                if (err.errResponse && err.errResponse.code == 4001) {
                    // トークン無効の場合は、リロード
                    err.needClearToken = true;
                    err.needCloseAndReload = true;
                }
            }

            throw err;
        }
    }
}
